import React from 'react';
import { Button } from '@packages/ui/primitives/Button';
import type { ButtonVariant } from '@packages/ui/primitives/Button';
import { Typography } from '@packages/ui/primitives/Typography';
import { Dropdown } from '@packages/ui/composites/Dropdown';
import type { InternalTableSchema, InternalActionDef } from '../schema/types';
import { ActionRegistry, type ActionExecutionContext } from '../../registry/ActionRegistry';
import { resolveSelectedRows } from '../../utils/selectionHelpers';
import type { ActionContext, PendingConfirmation } from '../../hooks/useActionExecutor';
import ColumnPickerV2 from './ColumnPickerV2';
import { TABLE_IDS } from '../semantic-contract';
import type { EventScope } from '@packages/block-events/core';

const BULK_BUTTON_VARIANT: Record<string, ButtonVariant> = {
  default: 'secondary',
  danger: 'secondary-red-outline',
};

function resolveBulkVariant(action: InternalActionDef): ButtonVariant {
  return BULK_BUTTON_VARIANT[action.variant] ?? 'secondary';
}

export interface TableBulkToolbarProps {
  isMobile: boolean;
  internalSchema: InternalTableSchema;
  effectiveSelectedKeys: string[];
  isAllPagesSelected: boolean;
  uncheckedIds: string[];
  effectiveTotalCount: number;
  showSelectionColumn: boolean;
  paginatedData: unknown[];
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  isGroupingEnabled: boolean;
  groupKeys: string[];
  allGroupsExpanded: boolean;
  computed: {
    hasEdits: boolean;
    editedRowKeys: string[];
  };

  handleSelectAllPages: () => void;
  handleClearAllPages: () => void;
  stateActions: {
    collapseAllGroups: () => void;
    expandAllGroups: (keys: string[]) => void;
    cancelAllEditing: () => void;
    toggleGroup: (key: string) => void;
  };

  bulk: ReadonlyArray<InternalActionDef>;
  buildTableQuery: () => Record<string, unknown>;
  registryRefreshTable: () => void;
  axiosInstance?: any;
  buildActionContext: (base: Partial<ActionContext> & { actionDef: InternalActionDef }) => ActionContext;
  executeAction: (action: InternalActionDef, context: ActionContext) => Promise<PendingConfirmation | null>;
  setPendingConfirm: (confirm: PendingConfirmation | null) => void;

  optionalColumns: Array<{ header: string; field: string }>;
  effectiveSelectedColumnList: string[] | undefined;
  effectiveOnColumnChange?: (cols: string[]) => void;
  effectiveColumnPrefLoading: boolean;

  handleBulkSave: () => Promise<void>;
  effectiveSaveLoading: boolean;
  executingActionIds: Set<string>;
  setExecutingActionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  eventScope?: EventScope | null;
  tableSemanticId?: string;
}

export function TableBulkToolbar({
  isMobile,
  internalSchema,
  effectiveSelectedKeys,
  isAllPagesSelected,
  uncheckedIds,
  effectiveTotalCount,
  showSelectionColumn,
  paginatedData,
  displayData,
  resolveRowKey,
  isGroupingEnabled,
  groupKeys,
  allGroupsExpanded,
  computed,
  handleSelectAllPages,
  handleClearAllPages,
  stateActions,
  bulk,
  buildTableQuery,
  registryRefreshTable,
  axiosInstance,
  buildActionContext,
  executeAction,
  setPendingConfirm,
  optionalColumns,
  effectiveSelectedColumnList,
  effectiveOnColumnChange,
  effectiveColumnPrefLoading,
  handleBulkSave,
  effectiveSaveLoading,
  executingActionIds,
  setExecutingActionIds,
  eventScope,
  tableSemanticId,
}: TableBulkToolbarProps): React.ReactElement | null {
  const selection = internalSchema.features.selection;
  const hasSelection = effectiveSelectedKeys.length > 0 || isAllPagesSelected;

  const showToolbar =
    hasSelection ||
    internalSchema.features.columnPreferences?.selectorEnabled === true ||
    (isGroupingEnabled && groupKeys.length > 0) ||
    (showSelectionColumn && selection.selectAllPages && paginatedData.length > 0) ||
    (computed.hasEdits &&
      (internalSchema.features.editing.bulkSave || computed.editedRowKeys.length > 1));

  if (!showToolbar) return null;

  const executeBulkAction = async (a: InternalActionDef) => {
    if (executingActionIds.has(a.id)) return;
    setExecutingActionIds((prev) => new Set(prev).add(a.id));
    try {
      const execContext: ActionExecutionContext = {
        pk: '',
        row: {},
        selectedKeys: effectiveSelectedKeys,
        selectedRows: isAllPagesSelected
          ? undefined
          : resolveSelectedRows(effectiveSelectedKeys, displayData, resolveRowKey),
        tableQuery: isAllPagesSelected ? buildTableQuery() : undefined,
        refreshTable: registryRefreshTable,
        clearSelection: handleClearAllPages,
        selectAllActive: isAllPagesSelected,
        deselectedRowIds: isAllPagesSelected ? uncheckedIds : undefined,
        totalCount: isAllPagesSelected ? effectiveTotalCount : undefined,
        axiosInstance,
        eventScope: eventScope ?? undefined,
        tableSemanticId,
      };

      if (ActionRegistry.hasAction(a.id)) {
        await ActionRegistry.executeAction(a.id, execContext);
        return;
      }

      const context = buildActionContext({
        selectedKeys: effectiveSelectedKeys,
        actionDef: a,
      });
      const confirmation = await executeAction(a, context);
      if (confirmation) setPendingConfirm(confirmation);
    } finally {
      setExecutingActionIds((prev) => {
        const next = new Set(prev);
        next.delete(a.id);
        return next;
      });
    }
  };

  const visibleBulk = bulk.filter((a) => {
    const action = ActionRegistry.resolveAction(a.id);
    if (action && !action.isAvailable()) return false;

    if (!isAllPagesSelected && effectiveSelectedKeys.length > 0 && action) {
      const selectedRows = resolveSelectedRows(
        effectiveSelectedKeys,
        displayData,
        resolveRowKey,
      );
      const hasActionableRow = selectedRows.some((row) => action.isActionable(row).visible);
      if (!hasActionableRow) return false;
    }

    return true;
  });

  return (
    <div
      data-testid='dt-v2-invitation-block'
      className='flex flex-wrap items-center gap-2 px-3 pt-2 sm:gap-3'>
      {isGroupingEnabled && groupKeys.length > 0 && (
        <Button
          label={allGroupsExpanded ? 'Collapse All' : 'Expand All'}
          type='button'
          variant='secondary-gray'
          size='sm'
          icon={allGroupsExpanded ? 'expand_less' : 'expand_more'}
          onClick={() => {
            if (allGroupsExpanded) {
              stateActions.collapseAllGroups();
            } else {
              stateActions.expandAllGroups(groupKeys);
            }
          }}
          semanticId={TABLE_IDS.toggleAllGroups}
        />
      )}

      {showSelectionColumn && selection.selectAllPages && paginatedData.length > 0 && (
        <Button
          label={isAllPagesSelected ? 'Deselect All' : 'Select All'}
          type='button'
          variant='secondary'
          size='sm'
          onClick={isAllPagesSelected ? handleClearAllPages : handleSelectAllPages}
          semanticId={TABLE_IDS.selectAllToggle}
        />
      )}

      {(effectiveSelectedKeys.length > 0 || isAllPagesSelected) &&
        selection.bulkActionsEnabled &&
        (() => {
          if (visibleBulk.length === 0) return null;

          if (isMobile && visibleBulk.length > 1) {
            return (
              <Dropdown.Root>
                <Dropdown.Trigger asChild>
                  <Button
                    variant='secondary'
                    size='sm'
                    icon='more_horiz'
                    type='button'
                    semanticId={TABLE_IDS.bulkAction('overflow-trigger')}
                  />
                </Dropdown.Trigger>
                <Dropdown.Content>
                  {visibleBulk.map((a) => (
                    <Dropdown.Item
                      key={a.id}
                      onSelect={() => executeBulkAction(a)}
                      semanticId={TABLE_IDS.bulkAction(a.id)}
                    >
                      {a.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Content>
              </Dropdown.Root>
            );
          }

          return visibleBulk.map((a) => (
            <Button
              key={a.id}
              label={`${a.label}`}
              type='button'
              variant={resolveBulkVariant(a)}
              size='sm'
              loading={executingActionIds.has(a.id)}
              disabled={executingActionIds.has(a.id)}
              onClick={() => executeBulkAction(a)}
              semanticId={TABLE_IDS.bulkAction(a.id)}
            />
          ));
        })()}

      {hasSelection &&
        selection.bulkActionsEnabled &&
        (() => {
          const selectedCount = isAllPagesSelected
            ? effectiveTotalCount - uncheckedIds.length
            : effectiveSelectedKeys.length;
          const entityName = internalSchema.entity ?? 'Row';
          const plural = selectedCount !== 1 ? 's' : '';

          const showOfTotal =
            effectiveTotalCount > 0 && selectedCount !== effectiveTotalCount;

          const label = isAllPagesSelected && uncheckedIds.length === 0
            ? `All ${selectedCount} ${entityName}${plural} Selected`
            : showOfTotal
              ? `${selectedCount} of ${effectiveTotalCount} ${entityName}${plural} Selected`
              : `${selectedCount} ${entityName}${plural} Selected`;

          return (
            <Typography
              variant='body-xs'
              color='var(--ui-text-muted)'
              className='shrink-0 whitespace-nowrap order-last basis-full sm:order-none sm:basis-auto'
              id={TABLE_IDS.selectionCount}
              data-testid='dt-v2-selection-count'
            >
              {label}
            </Typography>
          );
        })()}

      {internalSchema.features.columnPreferences?.selectorEnabled === true &&
        effectiveOnColumnChange && (
          <div data-testid='dt-v2-column-picker'>
            <ColumnPickerV2
              data={optionalColumns.map((col) => ({
                label: col.header,
                value: col.field,
              }))}
              handleUser={() => { }}
              checkedList={
                effectiveSelectedColumnList ??
                internalSchema.columns
                  .filter((c) => c.visibility !== 'picker' && c.visibility !== 'hidden')
                  .map((c) => c.field)
              }
              handleSubmit={() => { }}
              onSave={(finalList) => {
                effectiveOnColumnChange?.(finalList);
              }}
              loading={effectiveColumnPrefLoading}
              triggerLabel={isMobile ? '' : 'Add Columns'}
              triggerIcon='add_circle_outline'
              width='255px'
              padding='0px'
            />
          </div>
        )}

      {computed.hasEdits &&
        !internalSchema.features.editing.externalControl &&
        (internalSchema.features.editing.bulkSave ||
          computed.editedRowKeys.filter((k) => !k.includes('.')).length > 1) && (
          <>
            <Button
              label='Save'
              type='button'
              variant='secondary'
              size='sm'
              icon='save'
              onClick={handleBulkSave}
              loading={effectiveSaveLoading}
              semanticId={TABLE_IDS.bulkSave}
            />
            <Button
              label='Cancel'
              type='button'
              variant='secondary-dark'
              size='sm'
              icon='close'
              onClick={() => stateActions.cancelAllEditing()}
              semanticId={TABLE_IDS.bulkCancel}
            />
          </>
        )}
    </div>
  );
}
